import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMountain, faArrowLeft, faSave, faCalculator, faInfoCircle, faUserCheck, faComment, faUserShield, faTable, faUser, faCalendar, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { Spinner } from 'react-bootstrap';
import { getCustomers } from '../../../apis/customers';
import { getReviewers } from '../../../apis/reviewers';
import { createFineAggregateTestRequest } from '../../../apis/fineAggregate';

const FineAggregateForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentForm, setCurrentForm] = useState(1);
  const [selectedTests, setSelectedTests] = useState([]);
  
  // Backend Data State
  const [customers, setCustomers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingReviewers, setLoadingReviewers] = useState(true);
  
  // Save State
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Searchable Dropdown State
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');

  // Form Data State - Must be declared before useEffects that use it
  const [formData, setFormData] = useState({
    // General Information
    customer_id: '',
    sample_description: '',
    date_of_receipt: new Date().toISOString().split('T')[0],
    date_of_testing: new Date().toISOString().split('T')[0],
    sample_test_code: '',
    url_number: '',
    job_code_number: '',
    reference_number: '',
    type_size_aggregate: '',
    test_method: '',
    condition_of_aggregate: '',
    environmental_conditions: '',
    proposed_use: '',
    source: '',
    type_of_aggregate: '',
    max_nominal_size: '',
    period_of_testing: '',
    period_of_testing_start: '',
    period_of_testing_end: '',
    
    // Form 1 - Bulk Density
    volume_01: '',
    volume_02: '',
    volume_03: '',
    rodded_weight_01: '',
    rodded_weight_02: '',
    rodded_weight_03: '',
    rodded_density_01: '',
    rodded_density_02: '',
    rodded_density_03: '',
    loose_weight_01: '',
    loose_weight_02: '',
    loose_weight_03: '',
    loose_density_01: '',
    loose_density_02: '',
    loose_density_03: '',
    mean_rodded_density: '',
    mean_loose_density: '',
    
    // Form 2 - Sieve Analysis
    total_weight: '',
    sieve_data: {},
    // Sieve Analysis - Weight Retained (B1-B9, excluding Pan)
    sieve_weight_retained_01: '', // 10 mm
    sieve_weight_retained_02: '', // 4.75 mm
    sieve_weight_retained_03: '', // 2.36 mm
    sieve_weight_retained_04: '', // 1.18 mm
    sieve_weight_retained_05: '', // 600 mic.
    sieve_weight_retained_06: '', // 300 mic.
    sieve_weight_retained_07: '', // 150 mic.
    sieve_weight_retained_08: '', // 75 mic.
    sieve_weight_retained_09: '', // Pan
    // Sieve Analysis - % Weight Retained (C1-C9)
    sieve_percent_weight_retained_01: '',
    sieve_percent_weight_retained_02: '',
    sieve_percent_weight_retained_03: '',
    sieve_percent_weight_retained_04: '',
    sieve_percent_weight_retained_05: '',
    sieve_percent_weight_retained_06: '',
    sieve_percent_weight_retained_07: '',
    sieve_percent_weight_retained_08: '',
    sieve_percent_weight_retained_09: '',
    // Sieve Analysis - Cumulative % Weight Retained (D1-D9)
    sieve_cumulative_percent_retained_01: '',
    sieve_cumulative_percent_retained_02: '',
    sieve_cumulative_percent_retained_03: '',
    sieve_cumulative_percent_retained_04: '',
    sieve_cumulative_percent_retained_05: '',
    sieve_cumulative_percent_retained_06: '',
    sieve_cumulative_percent_retained_07: '',
    sieve_cumulative_percent_retained_08: '',
    sieve_cumulative_percent_retained_09: '',
    // Sieve Analysis - % Passing (E1-E9)
    sieve_percent_passing_01: '',
    sieve_percent_passing_02: '',
    sieve_percent_passing_03: '',
    sieve_percent_passing_04: '',
    sieve_percent_passing_05: '',
    sieve_percent_passing_06: '',
    sieve_percent_passing_07: '',
    sieve_percent_passing_08: '',
    sieve_percent_passing_09: '',
    // Sieve Analysis - Calculations
    sieve_sum_cumulative_excluding_pan: '',
    fineness_modulus: '',
    zone_result: '',
    
    // Form 3 - Specific Gravity
    specific_gravity_data: {},
    // Form 3 - Specific Gravity & Water Absorption
    saturated_weight_01: '',
    saturated_weight_02: '',
    saturated_weight_03: '',
    pycnometer_aggregate_water_01: '',
    pycnometer_aggregate_water_02: '',
    pycnometer_aggregate_water_03: '',
    pycnometer_water_01: '',
    pycnometer_water_02: '',
    pycnometer_water_03: '',
    oven_dried_weight_01: '',
    oven_dried_weight_02: '',
    oven_dried_weight_03: '',
    specific_gravity_01: '',
    specific_gravity_02: '',
    specific_gravity_03: '',
    mean_specific_gravity: '',
    water_absorption_01: '',
    water_absorption_02: '',
    water_absorption_03: '',
    mean_water_absorption: '',
    
    // Verification
    tested_by_name: '',
    tested_by_date: new Date().toISOString().split('T')[0],
    checked_by_name: '',
    checked_by_date: new Date().toISOString().split('T')[0],
    verified_by_name: 'Prakarsh A Sangave',
    verified_by_date: new Date().toISOString().split('T')[0],
    reviewed_by: 'Lalita S. Dussa - Quality Manager',
    reviewer_name: '',
    reviewer_designation: '',
    reviewer_graduation: '',
    authorized_by: 'Prakarsh Sangave',
    authorized_name: 'Mr. Prakarsh A Sangave',
    authorized_designation: 'Chief Executive Officer',
    authorized_qualification: 'M.E(Civil-Structures)',
    authorized_qualification_2: 'MTech (Civil-Geotechnical), M.I.E, F.I.E.',
    remarks: ''
  });

  // Fetch Data on Mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersData, reviewersData] = await Promise.all([
          getCustomers(),
          getReviewers()
        ]);
        setCustomers(customersData);
        setFilteredCustomers(customersData);
        setReviewers(reviewersData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingCustomers(false);
        setLoadingReviewers(false);
      }
    };
    fetchData();
  }, []);

  // Populate reviewer details when reviewers are loaded and reviewed_by is set
  useEffect(() => {
    if (reviewers.length > 0 && formData.reviewed_by && !formData.reviewer_name) {
      const selectedReviewer = reviewers.find(r => r.name === formData.reviewed_by);
      if (selectedReviewer) {
        setFormData(prev => ({
          ...prev,
          reviewer_name: selectedReviewer.name || prev.reviewed_by,
          reviewer_designation: selectedReviewer.designation || '',
          reviewer_graduation: selectedReviewer.graduation || ''
        }));
        console.log('✅ Auto-populated reviewer details from default value');
      }
    }
  }, [reviewers, formData.reviewed_by]);

  // Filter customers based on dropdown search term
  useEffect(() => {
    if (dropdownSearchTerm.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        customer.name.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
        (customer.phone && customer.phone.includes(dropdownSearchTerm)) ||
        (customer.city && customer.city.toLowerCase().includes(dropdownSearchTerm.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    }
  }, [dropdownSearchTerm, customers]);

  // Handle dropdown search input
  const handleDropdownSearch = (e) => {
    setDropdownSearchTerm(e.target.value);
  };

  // Handle customer input click to show dropdown
  const handleCustomerInputClick = () => {
    setShowCustomerDropdown(true);
    setDropdownSearchTerm(''); // Clear search when opening dropdown
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown]);

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      // Auto-fill other fields if they match
      // sample_description might generally be custom, keeping empty or previous
    }));
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(false);
  };

  // Handle selected tests from popup
  useEffect(() => {
    if (location.state && location.state.selectedTests) {
      setSelectedTests(location.state.selectedTests);
      // Set current form to first available test
      if (location.state.selectedTests.includes('bulkDensity')) {
        setCurrentForm(1);
      } else if (location.state.selectedTests.includes('sieveAnalysis')) {
        setCurrentForm(2);
      } else if (location.state.selectedTests.includes('specificGravity')) {
        setCurrentForm(3);
      }
    } else {
      // Default to all tests if no selection made
      setSelectedTests(['bulkDensity', 'sieveAnalysis', 'specificGravity']);
      setCurrentForm(1);
    }
  }, [location.state]);

  // Add CSS styles to match reference template
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .auto-calculated {
        background-color: #f8f9fa !important;
        color: #6c757d !important;
        cursor: not-allowed !important;
      }
      
      .auto-calculated:focus {
        box-shadow: none !important;
        border-color: #ced4da !important;
      }
      
      .loose-weight-input {
        background-color: white !important;
        color: #000 !important;
        cursor: text !important;
        pointer-events: auto !important;
      }
      
      .loose-weight-input:focus {
        background-color: white !important;
        border-color: #007bff !important;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
      }
      
      .table td,
      .table td *,
      .table input,
      .table strong,
      .table .form-control {
        text-align: center !important;
        vertical-align: middle !important;
      }
      
      .table td.left-align-text {
        text-align: left !important;
      }
      
      .table td.left-align-text strong {
        text-align: left !important;
      }
      
      .table th,
      .table th * {
        text-align: center !important;
        vertical-align: middle !important;
      }
      
      .fine-aggregate-header {
        background-color: #17a2b8 !important;
        color: white !important;
        border-radius: 15px !important;
      }
      
      .fine-aggregate-header .card-body {
        background-color: #17a2b8 !important;
        color: white !important;
        border-radius: 15px !important;
      }
      
      .fine-aggregate-header h1,
      .fine-aggregate-header p {
        color: white !important;
      }
      
      .step-navigation .btn-outline-primary {
        border: 2px solid #17a2b8 !important;
        background-color: transparent !important;
        color: #17a2b8 !important;
      }
      


      /* Unified Form Control and Select Styling to match Customer Name */
      .form-control-vitrag, .form-select {
        background-color: #2d3748 !important;
        border: 2px solid #4a5568 !important;
        border-radius: 10px !important;
        color: #e2e8f0 !important;
        padding: 10px 12px !important;
        font-size: 0.9rem !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        transition: all 0.3s ease !important;
      }

      /* Specific styling for Select to ensure arrow visibility */
      select.form-control-vitrag, .form-select {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23e2e8f0' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e") !important;
        background-repeat: no-repeat !important;
        background-position: right 0.75rem center !important;
        background-size: 16px 12px !important;
      }

      .form-control-vitrag:focus, .form-select:focus {
        border-color: #63b3ed !important;
        box-shadow: 0 0 0 3px rgba(99, 179, 237, 0.2) !important;
        outline: none !important;
        background-color: #2d3748 !important;
        color: #e2e8f0 !important;
      }

      /* Dropdown Options Styling */
      select option {
        background-color: #2d3748 !important;
        color: #e2e8f0 !important;
        padding: 10px !important;
      }

      /* Read-only inputs should look distinct but consistent */
      .form-control-vitrag[readonly], .form-control-vitrag[disabled] {
        background-color: #1a202c !important;
        opacity: 0.8 !important;
        cursor: not-allowed;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Auto-calculate bulk density when form data changes
  useEffect(() => {
    if (currentForm === 1 && formData) {
      // Recalculate all bulk densities when form data changes
      ['01', '02', '03'].forEach(testNum => {
        const volume = parseFloat(formData[`volume_${testNum}`] || 0);
        const roddedWeight = parseFloat(formData[`rodded_weight_${testNum}`] || 0);
        const looseWeight = parseFloat(formData[`loose_weight_${testNum}`] || 0);
        
        setFormData(prev => {
          const updates = {};
          
          // Calculate Rodded Bulk Density = Net weight of Rodded Sample (kg) / Volume of measure cylinder (lit.)
          if (volume > 0 && roddedWeight > 0) {
            const roddedDensity = roddedWeight / volume;
            updates[`rodded_density_${testNum}`] = roddedDensity.toFixed(3);
          } else {
            // Clear if either value is missing or zero
            updates[`rodded_density_${testNum}`] = '';
          }
          
          // Calculate Loose Bulk Density (kg/lit.) = Net weight of Loose Sample in the measure cylinder (kg) / Volume of measure cylinder used for determination of bulk density (lit.)
          if (volume > 0 && looseWeight > 0) {
            const looseDensity = looseWeight / volume;
            updates[`loose_density_${testNum}`] = looseDensity.toFixed(3);
          } else {
            // Clear if either value is missing or zero
            updates[`loose_density_${testNum}`] = '';
          }
          
          return { ...prev, ...updates };
        });
      });
      
      // Calculate mean values after a short delay to ensure all calculations are complete
      setTimeout(() => {
        calculateMeanValues();
      }, 100);
    }
  }, [formData?.volume_01, formData?.volume_02, formData?.volume_03, 
      formData?.rodded_weight_01, formData?.rodded_weight_02, formData?.rodded_weight_03,
      formData?.loose_weight_01, formData?.loose_weight_02, formData?.loose_weight_03, currentForm]);

  // Auto-calculate specific gravity and water absorption when form data changes (Form 3)
  useEffect(() => {
    if (currentForm === 3 && formData) {
      // Recalculate all specific gravity and water absorption when form data changes
      ['01', '02', '03'].forEach(testNum => {
        const A = parseFloat(formData[`saturated_weight_${testNum}`] || 0);
        const B = parseFloat(formData[`pycnometer_aggregate_water_${testNum}`] || 0);
        const C = parseFloat(formData[`pycnometer_water_${testNum}`] || 0);
        const D = parseFloat(formData[`oven_dried_weight_${testNum}`] || 0);
        
        setFormData(prev => {
          const updates = {};
          
          // Calculate Specific Gravity = [D/A-(B-C)]
          // Formula: D / (A - (B - C))
          const denominator = A - (B - C);
          if (A > 0 && B > 0 && C > 0 && D > 0 && denominator !== 0) {
            const specificGravity = D / denominator;
            updates[`specific_gravity_${testNum}`] = specificGravity.toFixed(3);
          } else {
            // Clear if values are missing or denominator is zero
            updates[`specific_gravity_${testNum}`] = '';
          }
          
          // Calculate Water Absorption = [(A-D)/D]*100
          if (A > 0 && D > 0) {
            const waterAbsorption = ((A - D) / D) * 100;
            updates[`water_absorption_${testNum}`] = waterAbsorption.toFixed(2);
          } else {
            // Clear if values are missing
            updates[`water_absorption_${testNum}`] = '';
          }
          
          return { ...prev, ...updates };
        });
      });
      
      // Calculate mean values
      setTimeout(() => {
        calculateForm3MeanValues();
      }, 100);
    }
  }, [formData?.saturated_weight_01, formData?.saturated_weight_02, formData?.saturated_weight_03,
      formData?.pycnometer_aggregate_water_01, formData?.pycnometer_aggregate_water_02, formData?.pycnometer_aggregate_water_03,
      formData?.pycnometer_water_01, formData?.pycnometer_water_02, formData?.pycnometer_water_03,
      formData?.oven_dried_weight_01, formData?.oven_dried_weight_02, formData?.oven_dried_weight_03, currentForm]);

  // Auto-calculate sieve analysis when weight retained values change (Form 2)
  useEffect(() => {
    if (currentForm === 2) {
      calculateSieveAnalysis();
    }
  }, [
    formData?.sieve_weight_retained_01, formData?.sieve_weight_retained_02, 
    formData?.sieve_weight_retained_03, formData?.sieve_weight_retained_04,
    formData?.sieve_weight_retained_05, formData?.sieve_weight_retained_06,
    formData?.sieve_weight_retained_07, formData?.sieve_weight_retained_08,
    formData?.sieve_weight_retained_09,
    currentForm
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('handleInputChange called with:', name, value, 'currentForm:', currentForm);
    
    setFormData(prev => {
      const updates = { [name]: value };
      
      // When reviewer is selected, populate reviewer details
      if (name === 'reviewed_by' && value) {
        const selectedReviewer = reviewers.find(r => r.name === value);
        if (selectedReviewer) {
          updates.reviewer_name = selectedReviewer.name || value;
          updates.reviewer_designation = selectedReviewer.designation || '';
          updates.reviewer_graduation = selectedReviewer.graduation || '';
          console.log('✅ Populated reviewer details:', {
            name: updates.reviewer_name,
            designation: updates.reviewer_designation,
            graduation: updates.reviewer_graduation
          });
        }
      }
      
      return { ...prev, ...updates };
    });
    
    // Auto-calculate bulk density if it's form 1
    if (currentForm === 1) {
      console.log('Calling calculateBulkDensity for form 1');
      calculateBulkDensity(name, value);
    }
    
    // Auto-calculate specific gravity and water absorption if it's form 3
    if (currentForm === 3) {
      console.log('Calling calculateSpecificGravityAndWaterAbsorption for form 3');
      calculateSpecificGravityAndWaterAbsorption(name, value);
    }
    
    // Auto-calculate sieve analysis if it's form 2 and weight retained field changed
    if (currentForm === 2 && name.startsWith('sieve_weight_retained_')) {
      setTimeout(() => {
        calculateSieveAnalysis();
      }, 100);
    }
  };

  const handlePeriodOfTestingChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Format dates as DD/MM/YYYY
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      
      // Combine dates into "DD/MM/YYYY to DD/MM/YYYY" format
      if (updated.period_of_testing_start && updated.period_of_testing_end) {
        updated.period_of_testing = `${formatDate(updated.period_of_testing_start)} to ${formatDate(updated.period_of_testing_end)}`;
      } else if (updated.period_of_testing_start || updated.period_of_testing_end) {
        // If only one date is selected, show partial format
        updated.period_of_testing = updated.period_of_testing_start 
          ? formatDate(updated.period_of_testing_start) 
          : formatDate(updated.period_of_testing_end);
      } else {
        updated.period_of_testing = '';
      }
      
      return updated;
    });
  };

  // Calculate Sieve Analysis values
  const calculateSieveAnalysis = () => {
    setFormData(prev => {
      const updates = {};
      
      // Get all weight retained values (B1-B9)
      const weights = [
        parseFloat(prev.sieve_weight_retained_01) || 0, // 10 mm
        parseFloat(prev.sieve_weight_retained_02) || 0, // 4.75 mm
        parseFloat(prev.sieve_weight_retained_03) || 0, // 2.36 mm
        parseFloat(prev.sieve_weight_retained_04) || 0, // 1.18 mm
        parseFloat(prev.sieve_weight_retained_05) || 0, // 600 mic.
        parseFloat(prev.sieve_weight_retained_06) || 0, // 300 mic.
        parseFloat(prev.sieve_weight_retained_07) || 0, // 150 mic.
        parseFloat(prev.sieve_weight_retained_08) || 0, // 75 mic.
        parseFloat(prev.sieve_weight_retained_09) || 0  // Pan
      ];
      
      // Calculate sum of all weights (B1 + B2 + ... + B9)
      const sumOfWeights = weights.reduce((sum, val) => sum + val, 0);
      
      // Calculate % Weight Retained (C) = (B / sum of B) * 100
      const percentWeightRetained = weights.map(weight => {
        if (sumOfWeights > 0 && weight > 0) {
          return (weight / sumOfWeights) * 100;
        }
        return 0;
      });
      
      // Update % Weight Retained fields (C1-C9)
      percentWeightRetained.forEach((value, index) => {
        updates[`sieve_percent_weight_retained_${String(index + 1).padStart(2, '0')}`] = 
          value > 0 ? value.toFixed(2) : '';
      });
      
      // Calculate Cumulative % Weight Retained (D)
      // D1 = C1
      // D2 = C1 + C2
      // D3 = C1 + C2 + C3
      // ... and so on
      const cumulativePercentRetained = [];
      let cumulativeSum = 0;
      
      percentWeightRetained.forEach((value, index) => {
        cumulativeSum += value;
        cumulativePercentRetained.push(cumulativeSum);
        updates[`sieve_cumulative_percent_retained_${String(index + 1).padStart(2, '0')}`] = 
          cumulativeSum > 0 ? cumulativeSum.toFixed(2) : '';
      });
      
      // Calculate % Passing (E) = 100 - D
      cumulativePercentRetained.forEach((value, index) => {
        const percentPassing = 100 - value;
        updates[`sieve_percent_passing_${String(index + 1).padStart(2, '0')}`] = 
          percentPassing >= 0 ? percentPassing.toFixed(2) : '';
      });
      
      // Calculate Sum of Cumulative % Weight Retained (Excluding Pan) = D1 + D2 + ... + D8
      // Excluding Pan means excluding index 8 (the 9th item)
      const sumCumulativeExcludingPan = cumulativePercentRetained
        .slice(0, 8) // Exclude Pan (index 8)
        .reduce((sum, val) => sum + val, 0);
      
      updates.sieve_sum_cumulative_excluding_pan = 
        sumCumulativeExcludingPan > 0 ? sumCumulativeExcludingPan.toFixed(2) : '';
      
      // Calculate Fineness Modulus = Sum of Cumulative % Weight Retained (Excluding Pan) / 100
      const finenessModulus = sumCumulativeExcludingPan / 100;
      updates.fineness_modulus = 
        finenessModulus > 0 ? finenessModulus.toFixed(2) : '';
      
      return { ...prev, ...updates };
    });
  };

  // Auto-calculation functions matching reference template

  const calculateMeanValues = () => {
    setFormData(prev => {
      // Calculate mean rodded bulk density = Sum of all Rodded Bulk Density values / 3
      const roddedDensity01 = parseFloat(prev.rodded_density_01) || 0;
      const roddedDensity02 = parseFloat(prev.rodded_density_02) || 0;
      const roddedDensity03 = parseFloat(prev.rodded_density_03) || 0;
      
      const sumRodded = roddedDensity01 + roddedDensity02 + roddedDensity03;
      const meanRodded = sumRodded > 0 ? (sumRodded / 3).toFixed(3) : '';
      
      // Calculate mean loose bulk density = Sum of all Loose Bulk Density values / 3
      const looseDensity01 = parseFloat(prev.loose_density_01) || 0;
      const looseDensity02 = parseFloat(prev.loose_density_02) || 0;
      const looseDensity03 = parseFloat(prev.loose_density_03) || 0;
      
      const sumLoose = looseDensity01 + looseDensity02 + looseDensity03;
      const meanLoose = sumLoose > 0 ? (sumLoose / 3).toFixed(3) : '';
      
      return {
        ...prev,
        mean_rodded_density: meanRodded,
        mean_loose_density: meanLoose
      };
    });
  };

  const calculateForm3MeanValues = () => {
    setFormData(prev => {
      // Calculate mean specific gravity
      const specificGravityValues = [
        parseFloat(prev.specific_gravity_01) || 0,
        parseFloat(prev.specific_gravity_02) || 0,
        parseFloat(prev.specific_gravity_03) || 0
      ].filter(val => val > 0);
      
      let meanSpecificGravity = '';
      if (specificGravityValues.length > 0) {
        meanSpecificGravity = (specificGravityValues.reduce((sum, val) => sum + val, 0) / specificGravityValues.length).toFixed(3);
      }
      
      // Calculate mean water absorption
      const waterAbsorptionValues = [
        parseFloat(prev.water_absorption_01) || 0,
        parseFloat(prev.water_absorption_02) || 0,
        parseFloat(prev.water_absorption_03) || 0
      ].filter(val => val > 0);
      
      let meanWaterAbsorption = '';
      if (waterAbsorptionValues.length > 0) {
        meanWaterAbsorption = (waterAbsorptionValues.reduce((sum, val) => sum + val, 0) / waterAbsorptionValues.length).toFixed(2);
      }
      
      return {
        ...prev,
        mean_specific_gravity: meanSpecificGravity,
        mean_water_absorption: meanWaterAbsorption
      };
    });
  };

  const calculateBulkDensity = (fieldName, value) => {
    console.log('calculateBulkDensity called with:', fieldName, value);
    
    if (fieldName.includes('volume_') || fieldName.includes('rodded_weight_') || fieldName.includes('loose_weight_')) {
      const testNum = fieldName.split('_')[1];
      console.log('Test number:', testNum);
      
      setFormData(prev => {
        const updatedData = { ...prev, [fieldName]: value };
        
        const volume = parseFloat(updatedData[`volume_${testNum}`] || 0);
        const roddedWeight = parseFloat(updatedData[`rodded_weight_${testNum}`] || 0);
        const looseWeight = parseFloat(updatedData[`loose_weight_${testNum}`] || 0);
        
        console.log('Values for test', testNum, ':', { volume, roddedWeight, looseWeight });
        
        // Calculate Rodded Bulk Density (kg/lit.) = Net weight of Rodded Sample (kg) / Volume of measure cylinder (lit.)
        if (volume > 0 && roddedWeight > 0) {
          const roddedDensity = roddedWeight / volume;
          updatedData[`rodded_density_${testNum}`] = roddedDensity.toFixed(3);
          console.log('Calculated rodded density:', roddedDensity.toFixed(3), '(Formula: ', roddedWeight, '/', volume, ')');
        } else {
          // Clear if either value is missing or zero
          updatedData[`rodded_density_${testNum}`] = '';
        }
        
        // Calculate Loose Bulk Density (kg/lit.) = Net weight of Loose Sample in the measure cylinder (kg) / Volume of measure cylinder used for determination of bulk density (lit.)
        if (volume > 0 && looseWeight > 0) {
          const looseDensity = looseWeight / volume;
          updatedData[`loose_density_${testNum}`] = looseDensity.toFixed(3);
          console.log('Calculated loose density:', looseDensity.toFixed(3), '(Formula: ', looseWeight, '/', volume, ')');
        } else {
          // Clear if either value is missing or zero
          updatedData[`loose_density_${testNum}`] = '';
        }
        
        return updatedData;
      });
    }
  };

  const calculateSpecificGravityAndWaterAbsorption = (fieldName, value) => {
    console.log('calculateSpecificGravityAndWaterAbsorption called with:', fieldName, value);
    
    if (fieldName.includes('saturated_weight_') || fieldName.includes('pycnometer_aggregate_water_') || 
        fieldName.includes('pycnometer_water_') || fieldName.includes('oven_dried_weight_')) {
      const testNum = fieldName.split('_')[1];
      console.log('Test number:', testNum);
      
      setFormData(prev => {
        const updatedData = { ...prev, [fieldName]: value };
        
        const A = parseFloat(updatedData[`saturated_weight_${testNum}`] || 0); // Saturated Surface Dry
        const B = parseFloat(updatedData[`pycnometer_aggregate_water_${testNum}`] || 0); // Pycnometer + Aggregate + Water
        const C = parseFloat(updatedData[`pycnometer_water_${testNum}`] || 0); // Pycnometer + Water
        const D = parseFloat(updatedData[`oven_dried_weight_${testNum}`] || 0); // Oven Dried
        
        console.log('Values for test', testNum, ':', { A, B, C, D });
        
        // Calculate Specific Gravity = [D/A-(B-C)]
        // Formula: D / (A - (B - C))
        const denominator = A - (B - C);
        if (A > 0 && B > 0 && C > 0 && D > 0 && denominator !== 0) {
          const specificGravity = D / denominator;
          updatedData[`specific_gravity_${testNum}`] = specificGravity.toFixed(3);
          console.log('Calculated specific gravity:', specificGravity.toFixed(3), '(Formula: ', D, '/ (', A, '- (', B, '-', C, ')))');
        } else {
          // Clear if values are missing or denominator is zero
          updatedData[`specific_gravity_${testNum}`] = '';
        }
        
        // Calculate Water Absorption = [(A-D)/D]*100
        if (A > 0 && D > 0) {
          const waterAbsorption = ((A - D) / D) * 100;
          updatedData[`water_absorption_${testNum}`] = waterAbsorption.toFixed(2);
          console.log('Calculated water absorption:', waterAbsorption.toFixed(2), '(Formula: ((', A, '-', D, ') /', D, ') * 100)');
        } else {
          // Clear if values are missing
          updatedData[`water_absorption_${testNum}`] = '';
        }
        
        return updatedData;
      });
    }
  };

  const renderForm1 = () => (
    <div>
      {/* General Information */}
      <Row className="mb-4">
        <Col>
          <Card className="card-vitrag shadow-sm">
            <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
              <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                General Information
              </h5>
            </Card.Header>
            <Card.Body className="transparent-input-section">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Customer Name *</Form.Label>
                    {loadingCustomers ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Loading customers...</span>
                      </div>
                    ) : (
                      <div className="customer-dropdown-container" style={{ position: 'relative' }}>
                        <Form.Control
                          type="text"
                          name="customerName"
                          value={customerSearchTerm}
                          onChange={(e) => setCustomerSearchTerm(e.target.value)}
                          onClick={handleCustomerInputClick}
                          placeholder="Search and select customer..."
                          required
                          autoComplete="off"
                          className="form-control-vitrag"
                          style={{ cursor: 'pointer' }}
                        />
                        {showCustomerDropdown && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: '#2d3748',
                              border: '2px solid #4a5568',
                              borderTop: 'none',
                              borderRadius: '0 0 10px 10px',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                            }}
                          >
                            <div style={{ padding: '15px', borderBottom: '1px solid #4a5568' }}>
                              <Form.Control
                                type="text"
                                placeholder="Search customers..."
                                value={dropdownSearchTerm}
                                onChange={handleDropdownSearch}
                                autoComplete="off"
                                style={{
                                  backgroundColor: '#1a202c',
                                  border: '2px solid #4a5568',
                                  borderRadius: '8px',
                                  color: '#e2e8f0'
                                }}
                              />
                            </div>
                            {filteredCustomers.length > 0 ? (
                              filteredCustomers.map((customer) => (
                                <div
                                  key={customer.id}
                                  onClick={() => handleCustomerSelect(customer)}
                                  style={{
                                    padding: '10px 15px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #495057',
                                    color: 'white',
                                    backgroundColor: 'transparent'
                                  }}
                                  onMouseEnter={(e) => { e.target.style.backgroundColor = '#495057'; }}
                                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
                                >
                                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>{customer.name}</div>
                                  <div style={{ fontSize: '0.85em', color: '#adb5bd' }}>
                                    {customer.phone && `${customer.phone} • `}
                                    {customer.city}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '15px', color: '#adb5bd', textAlign: 'center' }}>
                                {dropdownSearchTerm.trim() !== '' 
                                  ? `No customers found matching "${dropdownSearchTerm}"`
                                  : 'No customers available'
                                }
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Sample Description *</Form.Label>
                    <Form.Select name="sample_description" value={formData.sample_description} onChange={handleInputChange} className="form-control-vitrag" required>
                      <option value="">-- Select Sample Description --</option>
                      <option value="C Sand">C Sand</option>
                      <option value="M Sand">M Sand</option>
                      <option value="River Sand">River Sand</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Receipt *</Form.Label>
                    <Form.Control type="date" name="date_of_receipt" value={formData.date_of_receipt} onChange={handleInputChange} className="form-control-vitrag" required />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Type & Size of Aggregate *</Form.Label>
                    <Form.Control type="text" name="type_size_aggregate" value={formData.type_size_aggregate} onChange={handleInputChange} className="form-control-vitrag" placeholder="" required />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Test Method *</Form.Label>
                    <Form.Control type="text" name="test_method" value={formData.test_method} onChange={handleInputChange} className="form-control-vitrag" placeholder="" required />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sample Test Code Number *</Form.Label>
                    <Form.Control type="text" name="sample_test_code" value={formData.sample_test_code} onChange={handleInputChange} className="form-control-vitrag" placeholder="" required />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Testing *</Form.Label>
                    <Form.Control type="date" name="date_of_testing" value={formData.date_of_testing} onChange={handleInputChange} className="form-control-vitrag" required />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Condition of Aggregate *</Form.Label>
                    <Form.Control type="text" name="condition_of_aggregate" value={formData.condition_of_aggregate} onChange={handleInputChange} className="form-control-vitrag" placeholder="" required />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Environmental Conditions *</Form.Label>
                    <Form.Control type="text" name="environmental_conditions" value={formData.environmental_conditions} onChange={handleInputChange} className="form-control-vitrag" placeholder="" required />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Test Report Details */}
      <Row className="mb-4">
        <Col>
          <Card className="card-vitrag">
            <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
              <h4 style={{ color: 'var(--vitrag-gold)', margin: 0 }}><FontAwesomeIcon icon={faTable} className="me-2" />Test Report Details</h4>
            </Card.Header>
            <Card.Body className="transparent-input-section">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Report</Form.Label>
                    <Form.Control type="date" name="date_of_report" value={formData.date_of_report} onChange={handleInputChange} className="form-control-vitrag" />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Reference Number</Form.Label>
                    <Form.Control type="text" name="reference_number" value={formData.reference_number} onChange={handleInputChange} className="form-control-vitrag" placeholder="" />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Material Receipt</Form.Label>
                    <Form.Control type="date" name="date_of_material_receipt" value={formData.date_of_material_receipt} onChange={handleInputChange} className="form-control-vitrag" />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Material Description</Form.Label>
                    <Form.Control as="textarea" rows={2} name="material_description" value={formData.material_description} onChange={handleInputChange} className="form-control-vitrag" placeholder="" />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Location of Testing</Form.Label>
                    <Form.Control type="text" name="location_of_testing" value={formData.location_of_testing} onChange={handleInputChange} className="form-control-vitrag" placeholder="" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ULR Number *</Form.Label>
                    <Form.Control type="text" name="url_number" value={formData.url_number} onChange={handleInputChange} className="form-control-vitrag" placeholder="" required />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Job Code Number *</Form.Label>
                    <Form.Control type="text" name="job_code_number" value={formData.job_code_number} onChange={handleInputChange} className="form-control-vitrag" placeholder="" required />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Period of Testing</Form.Label>
                    <Row>
                      <Col md={5}>
                        <Form.Control 
                          type="date" 
                          name="period_of_testing_start" 
                          value={formData.period_of_testing_start} 
                          onChange={(e) => handlePeriodOfTestingChange('period_of_testing_start', e.target.value)} 
                          placeholder="Start Date" 
                          className="form-control-vitrag"
                        />
                      </Col>
                      <Col md={2} className="text-center d-flex align-items-center justify-content-center">
                        <span style={{ color: '#e2e8f0', fontWeight: 'bold' }}>to</span>
                      </Col>
                      <Col md={5}>
                        <Form.Control 
                          type="date" 
                          name="period_of_testing_end" 
                          value={formData.period_of_testing_end} 
                          onChange={(e) => handlePeriodOfTestingChange('period_of_testing_end', e.target.value)} 
                          placeholder="End Date" 
                          className="form-control-vitrag"
                        />
                      </Col>
                    </Row>
                    {formData.period_of_testing && (
                      <Form.Text className="text-muted" style={{ color: '#adb5bd', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                        {formData.period_of_testing}
                      </Form.Text>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Condition of Sample</Form.Label>
                    <Form.Control type="text" name="condition_of_sample" value={formData.condition_of_sample} onChange={handleInputChange} className="form-control-vitrag" placeholder="" />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Test Result */}
      <Row className="mb-4" style={{ border: 'none !important', outline: 'none !important' }}>
        <Col style={{ border: 'none !important', outline: 'none !important' }}>
          <div style={{ border: 'none !important', boxShadow: 'none !important', background: 'transparent !important', outline: 'none !important', margin: '0 !important', padding: '0 !important' }}>
            <div style={{ border: 'none !important', background: 'transparent !important', padding: '10px 0', outline: 'none !important' }}>
              <h5 className="mb-0"><FontAwesomeIcon icon={faTable} className="me-2" />Test Result</h5>
            </div>
            <div style={{ border: 'none !important', padding: '8px', background: 'transparent !important', outline: 'none !important' }}>
              <div className="table-responsive">
                <table className="table transparent-table" style={{ fontSize: '1.1rem', marginBottom: 0, tableLayout: 'fixed', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: '550px', padding: '6px', border: '1px solid #dee2e6', fontSize: '1.3rem' }}>Number of Tests</th>
                      <th style={{ width: '140px', padding: '4px', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #dee2e6', fontSize: '1.3rem' }}>01</th>
                      <th style={{ width: '140px', padding: '4px', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #dee2e6', fontSize: '1.3rem' }}>02</th>
                      <th style={{ width: '140px', padding: '4px', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #dee2e6', fontSize: '1.3rem' }}>03</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', paddingLeft: '12px', border: '1px solid #dee2e6' }}><strong>Volume of measure cylinder used for determination of bulk density (lit.)</strong></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.01" name="volume_01" value={formData.volume_01} onChange={handleInputChange} className="form-control-vitrag" style={{ height: '30px' }} /></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.01" name="volume_02" value={formData.volume_02} onChange={handleInputChange} className="form-control-vitrag" style={{ height: '30px' }} /></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }} className="text-center"><Form.Control type="number" step="0.01" name="volume_03" value={formData.volume_03} onChange={handleInputChange} className="form-control-vitrag" style={{ height: '30px' }} /></td>
                    </tr>
                    <tr>
                      <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', paddingLeft: '12px', border: '1px solid #dee2e6' }}><strong>Net weight of Rodded Sample in the measure cylinder (kg)</strong></td>
                      <td style={{ width: '60px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.001" name="rodded_weight_01" value={formData.rodded_weight_01} onChange={handleInputChange} className="form-control-vitrag" style={{ height: '30px' }} /></td>
                      <td style={{ width: '60px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.001" name="rodded_weight_02" value={formData.rodded_weight_02} onChange={handleInputChange} className="form-control-vitrag" style={{ height: '30px' }} /></td>
                      <td style={{ width: '60px', padding: '4px', border: '1px solid #dee2e6' }} className="text-center"><Form.Control type="number" step="0.001" name="rodded_weight_03" value={formData.rodded_weight_03} onChange={handleInputChange} className="form-control-vitrag" style={{ height: '30px' }} /></td>
                    </tr>
                    <tr>
                      <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', paddingLeft: '12px', border: '1px solid #dee2e6' }}><strong>Rodded Bulk Density (kg/lit.)</strong></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.001" value={formData.rodded_density_01 || ''} readOnly style={{ height: '30px', backgroundColor: '#f8f9fa' }} /></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.001" value={formData.rodded_density_02 || ''} readOnly style={{ height: '30px', backgroundColor: '#f8f9fa' }} /></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }} className="text-center"><Form.Control type="number" step="0.001" value={formData.rodded_density_03 || ''} readOnly style={{ height: '30px', backgroundColor: '#f8f9fa' }} /></td>
                    </tr>
                    <tr className="table-info">
                      <td style={{ fontSize: '1rem', padding: '6px', textAlign: 'center', border: '1px solid #dee2e6' }}><strong>Mean Rodded Bulk Density (kg/lit.)</strong></td>
                      <td colSpan="3" style={{ padding: '6px', border: '1px solid #dee2e6', backgroundColor: '#1C2333' }}><Form.Control type="number" step="0.001" value={formData.mean_rodded_density || ''} readOnly className="average-input-transparent" style={{ height: '30px', backgroundColor: 'var(--bs-dark)', background: 'var(--bs-dark)', backgroundImage: 'none', border: '1px solid var(--bs-gray-700)', color: '#ffffff', WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', boxShadow: 'none', outline: 'none' }} /></td>
                    </tr>
                    <tr>
                      <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', paddingLeft: '12px', border: '1px solid #dee2e6' }}><strong>Net weight of Loose Sample in the measure cylinder (kg)</strong></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.001" name="loose_weight_01" value={formData.loose_weight_01} onChange={handleInputChange} className="form-control-vitrag" style={{ height: '30px' }} /></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.001" name="loose_weight_02" value={formData.loose_weight_02} onChange={handleInputChange} className="form-control-vitrag" style={{ height: '30px' }} /></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }} className="text-center"><Form.Control type="number" step="0.001" name="loose_weight_03" value={formData.loose_weight_03} onChange={handleInputChange} className="form-control-vitrag" style={{ height: '30px' }} /></td>
                    </tr>
                    <tr>
                      <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', paddingLeft: '12px', border: '1px solid #dee2e6' }}><strong>Loose Bulk Density (kg/lit.)</strong></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.001" value={formData.loose_density_01 || ''} readOnly style={{ height: '30px', backgroundColor: '#f8f9fa' }} /></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }}><Form.Control type="number" step="0.001" value={formData.loose_density_02 || ''} readOnly style={{ height: '30px', backgroundColor: '#f8f9fa' }} /></td>
                      <td style={{ width: '140px', padding: '4px', border: '1px solid #dee2e6' }} className="text-center"><Form.Control type="number" step="0.001" value={formData.loose_density_03 || ''} readOnly style={{ height: '30px', backgroundColor: '#f8f9fa' }} /></td>
                    </tr>
                    <tr className="table-info">
                      <td style={{ fontSize: '1rem', padding: '6px', textAlign: 'center', border: '1px solid #dee2e6' }}><strong>Mean Loose Bulk Density (kg/lit.)</strong></td>
                      <td colSpan="3" style={{ padding: '6px', border: '1px solid #dee2e6', backgroundColor: '#1C2333' }}><Form.Control type="number" step="0.001" value={formData.mean_loose_density || ''} readOnly className="average-input-transparent" style={{ height: '30px', backgroundColor: 'var(--bs-dark)', background: 'var(--bs-dark)', backgroundImage: 'none', border: '1px solid var(--bs-gray-700)', color: '#ffffff', WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', boxShadow: 'none', outline: 'none' }} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  const renderForm2 = () => (
    <div>
      {/* General Information Section */}
      <Row className="mb-4">
        <Col>
          <Card className="card-vitrag shadow-sm">
            <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
              <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                General Information
              </h5>
            </Card.Header>
            <Card.Body className="transparent-input-section">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Customer Name *</Form.Label>
                    {loadingCustomers ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Loading customers...</span>
                      </div>
                    ) : (
                      <div className="customer-dropdown-container" style={{ position: 'relative' }}>
                        <Form.Control
                          type="text"
                          name="customerName"
                          value={customerSearchTerm}
                          onChange={(e) => setCustomerSearchTerm(e.target.value)}
                          onClick={handleCustomerInputClick}
                          placeholder="Search and select customer..."
                          required
                          autoComplete="off"
                          className="form-control-vitrag"
                          style={{ cursor: 'pointer' }}
                        />
                        {showCustomerDropdown && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: '#2d3748',
                              border: '2px solid #4a5568',
                              borderTop: 'none',
                              borderRadius: '0 0 10px 10px',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                            }}
                          >
                            <div style={{ padding: '15px', borderBottom: '1px solid #4a5568' }}>
                              <Form.Control
                                type="text"
                                placeholder="Search customers..."
                                value={dropdownSearchTerm}
                                onChange={handleDropdownSearch}
                                autoComplete="off"
                                style={{
                                  backgroundColor: '#1a202c',
                                  border: '2px solid #4a5568',
                                  borderRadius: '8px',
                                  color: '#e2e8f0'
                                }}
                              />
                            </div>
                            {filteredCustomers.length > 0 ? (
                              filteredCustomers.map((customer) => (
                                <div
                                  key={customer.id}
                                  onClick={() => handleCustomerSelect(customer)}
                                  style={{
                                    padding: '10px 15px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #495057',
                                    color: 'white',
                                    backgroundColor: 'transparent'
                                  }}
                                  onMouseEnter={(e) => { e.target.style.backgroundColor = '#495057'; }}
                                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
                                >
                                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>{customer.name}</div>
                                  <div style={{ fontSize: '0.85em', color: '#adb5bd' }}>
                                    {customer.phone && `${customer.phone} • `}
                                    {customer.city}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '15px', color: '#adb5bd', textAlign: 'center' }}>
                                {dropdownSearchTerm.trim() !== '' 
                                  ? `No customers found matching "${dropdownSearchTerm}"`
                                  : 'No customers available'
                                }
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Sample Description *</Form.Label>
                    <Form.Select 
                      name="sample_description" 
                      value={formData.sample_description} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      required 
                    >
                      <option value="">-- Select Sample Description --</option>
                      <option value="C Sand">C Sand</option>
                      <option value="M Sand">M Sand</option>
                      <option value="River Sand">River Sand</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Receipt *</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="date_of_receipt" 
                      value={formData.date_of_receipt} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      required 
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Proposed Use *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="proposed_use" 
                      value={formData.proposed_use} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Test Method *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="test_method" 
                      value={formData.test_method} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sample Test Code Number *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="sample_test_code" 
                      value={formData.sample_test_code} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required 
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Testing *</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="date_of_testing" 
                      value={formData.date_of_testing} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      required 
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Source *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="source" 
                      value={formData.source} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required 
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Environmental Conditions *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="environmental_conditions" 
                      value={formData.environmental_conditions} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Test Result Section */}
      <Row className="mb-4">
        <Col>
          <div style={{ padding: '10px 0' }}>
            <h5 className="mb-0"><FontAwesomeIcon icon={faTable} className="me-2" />Test Result</h5>
          </div>
          
          {/* Total Weight Input */}
              <Row className="justify-content-center mb-4">
                <Col md={8}>
                  <div className="input-group justify-content-center" style={{ maxWidth: '500px', margin: '0 auto' }}>
                    <span className="input-group-text" style={{ fontWeight: 'bold', backgroundColor: '#495057', color: 'white' }}>
                      Total Weight of Sample taken =
                    </span>
                <Form.Control 
                  type="number" 
                  step="0.01" 
                  placeholder="" 
                  name="total_weight" 
                  value={formData.total_weight} 
                  onChange={handleInputChange} 
                  className="form-control-vitrag"
                />
                    <span className="input-group-text" style={{ backgroundColor: '#495057', color: 'white' }}>gm</span>
                  </div>
                </Col>
              </Row>
              
          {/* Main Data Table */}
              <div className="table-responsive">
            <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'auto', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                  <thead className="table-dark">
                    <tr>
                  <th rowSpan="2" style={{ width: '75px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>IS Sieve</th>
                  <th rowSpan="2" style={{ width: '75px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Weigh. Retained (gm)</th>
                  <th rowSpan="2" style={{ width: '75px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>% Weight Retained</th>
                  <th rowSpan="2" style={{ width: '85px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Cumulative % Weight Retained</th>
                  <th rowSpan="2" style={{ width: '55px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>% Passing</th>
                  <th colSpan="4" style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontSize: '1rem' }}>Zoning as per Table 9 of IS 383</th>
                    </tr>
                    <tr>
                  <th style={{ width: '65px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontSize: '0.9rem' }}>Zone I</th>
                  <th style={{ width: '65px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontSize: '0.9rem' }}>Zone II</th>
                  <th style={{ width: '65px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontSize: '0.9rem' }}>Zone III</th>
                  <th style={{ width: '65px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontSize: '0.9rem' }}>Zone IV</th>
                    </tr>
                  </thead>
                  <tbody>
                {[
                  { sieve: '10 mm', zones: ['100', '100', '100', '100'], index: 1 },
                  { sieve: '4.75 mm', zones: ['90-100', '90-100', '90-100', '95-100'], index: 2 },
                  { sieve: '2.36 mm', zones: ['60-95', '75-100', '85-100', '95-100'], index: 3 },
                  { sieve: '1.18 mm', zones: ['30-70', '55-90', '75-100', '90-100'], index: 4 },
                  { sieve: '600 mic.', zones: ['15-34', '35-59', '60-79', '80-100'], index: 5 },
                  { sieve: '300 mic.', zones: ['5-20', '8-30', '12-40', '15-50'], index: 6 },
                  { sieve: '150 mic.', zones: ['---', '---', '---', '---'], index: 7 },
                  { sieve: '75 mic.', zones: ['---', '---', '---', '---'], index: 8 },
                  { sieve: 'Pan', zones: ['---', '---', '---', '---'], index: 9 }
                ].map((row, arrayIndex) => {
                  const fieldIndex = String(row.index).padStart(2, '0');
                  return (
                    <tr key={arrayIndex}>
                      <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontWeight: 'bold' }}>{row.sieve}</td>
                      {/* Weight Retained (B) - Input field */}
                      <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control 
                          type="number" 
                          step="0.01" 
                          name={`sieve_weight_retained_${fieldIndex}`}
                          value={formData[`sieve_weight_retained_${fieldIndex}`] || ''}
                          onChange={handleInputChange}
                          className="form-control form-control-sm" 
                          style={{ height: '25px', border: 'none', textAlign: 'center' }}
                        />
                      </td>
                      {/* % Weight Retained (C) - Calculated, Read-only */}
                      <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control 
                          type="number" 
                          step="0.01" 
                          value={formData[`sieve_percent_weight_retained_${fieldIndex}`] || ''}
                          readOnly
                          className="form-control form-control-sm" 
                          style={{ height: '25px', border: 'none', textAlign: 'center', backgroundColor: '#f8f9fa' }}
                        />
                      </td>
                      {/* Cumulative % Weight Retained (D) - Calculated, Read-only */}
                      <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control 
                          type="number" 
                          step="0.01" 
                          value={formData[`sieve_cumulative_percent_retained_${fieldIndex}`] || ''}
                          readOnly
                          className="form-control form-control-sm" 
                          style={{ height: '25px', border: 'none', textAlign: 'center', backgroundColor: '#f8f9fa' }}
                        />
                      </td>
                      {/* % Passing (E) - Calculated, Read-only */}
                      <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control 
                          type="number" 
                          step="0.01" 
                          value={formData[`sieve_percent_passing_${fieldIndex}`] || ''}
                          readOnly
                          className="form-control form-control-sm" 
                          style={{ height: '25px', border: 'none', textAlign: 'center', backgroundColor: '#f8f9fa' }}
                        />
                      </td>
                      {row.zones.map((zone, zoneIndex) => (
                        <td key={zoneIndex} style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                          {zone}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                
                {/* Calculation Rows */}
                <tr>
                  <td colSpan="5" style={{ padding: '8px', border: '1px solid #dee2e6', fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'left', verticalAlign: 'middle' }}>
                    Sum of Cumulative % Weight Retained (Excluding Pan) =
                  </td>
                  <td colSpan="4" style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      value={formData.sieve_sum_cumulative_excluding_pan || ''}
                      readOnly
                      className="form-control form-control-sm" 
                      style={{ height: '30px', textAlign: 'center', backgroundColor: '#f8f9fa', fontWeight: 'bold' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td colSpan="5" style={{ padding: '8px', border: '1px solid #dee2e6', fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'left', verticalAlign: 'middle' }}>
                    Fineness Modulus = (Sum of Cumulative % weight retained/100) =
                  </td>
                  <td colSpan="4" style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      value={formData.fineness_modulus || ''}
                      readOnly
                      className="form-control form-control-sm" 
                      style={{ height: '30px', textAlign: 'center', backgroundColor: '#f8f9fa', fontWeight: 'bold' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td colSpan="5" style={{ padding: '8px', border: '1px solid #dee2e6', fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'left', verticalAlign: 'middle' }}>
                    Zone to which Fine Aggregate belongs as per IS 383 =
                  </td>
                  <td colSpan="4" style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="text" 
                      name="zone_result"
                      value={formData.zone_result || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      required
                      style={{ height: '30px', textAlign: 'center' }}
                    />
                  </td>
                </tr>
                  </tbody>
                </table>
              </div>
        </Col>
      </Row>
    </div>
  );

  const renderForm3 = () => (
    <div>
      {/* General Information Section */}
      <Row className="mb-4">
        <Col>
          <Card className="card-vitrag shadow-sm">
            <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
              <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                General Information
              </h5>
            </Card.Header>
            <Card.Body className="transparent-input-section">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Customer Name *</Form.Label>
                    {loadingCustomers ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Loading customers...</span>
                      </div>
                    ) : (
                      <div className="customer-dropdown-container" style={{ position: 'relative' }}>
                        <Form.Control
                          type="text"
                          name="customerName"
                          value={customerSearchTerm}
                          onChange={(e) => setCustomerSearchTerm(e.target.value)}
                          onClick={handleCustomerInputClick}
                          placeholder="Search and select customer..."
                          required
                          autoComplete="off"
                          className="form-control-vitrag"
                          style={{ cursor: 'pointer' }}
                        />
                        {showCustomerDropdown && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: '#2d3748',
                              border: '2px solid #4a5568',
                              borderTop: 'none',
                              borderRadius: '0 0 10px 10px',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                            }}
                          >
                            <div style={{ padding: '15px', borderBottom: '1px solid #4a5568' }}>
                              <Form.Control
                                type="text"
                                placeholder="Search customers..."
                                value={dropdownSearchTerm}
                                onChange={handleDropdownSearch}
                                autoComplete="off"
                                style={{
                                  backgroundColor: '#1a202c',
                                  border: '2px solid #4a5568',
                                  borderRadius: '8px',
                                  color: '#e2e8f0'
                                }}
                              />
                            </div>
                            {filteredCustomers.length > 0 ? (
                              filteredCustomers.map((customer) => (
                                <div
                                  key={customer.id}
                                  onClick={() => handleCustomerSelect(customer)}
                                  style={{
                                    padding: '10px 15px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #495057',
                                    color: 'white',
                                    backgroundColor: 'transparent'
                                  }}
                                  onMouseEnter={(e) => { e.target.style.backgroundColor = '#495057'; }}
                                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
                                >
                                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>{customer.name}</div>
                                  <div style={{ fontSize: '0.85em', color: '#adb5bd' }}>
                                    {customer.phone && `${customer.phone} • `}
                                    {customer.city}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '15px', color: '#adb5bd', textAlign: 'center' }}>
                                {dropdownSearchTerm.trim() !== '' 
                                  ? `No customers found matching "${dropdownSearchTerm}"`
                                  : 'No customers available'
                                }
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Sample Description *</Form.Label>
                    <Form.Select 
                      name="sample_description" 
                      value={formData.sample_description} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      required 
                    >
                      <option value="">-- Select Sample Description --</option>
                      <option value="C Sand">C Sand</option>
                      <option value="M Sand">M Sand</option>
                      <option value="River Sand">River Sand</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Receipt *</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="date_of_receipt" 
                      value={formData.date_of_receipt} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      required 
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Type of Aggregate *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="type_of_aggregate" 
                      value={formData.type_of_aggregate} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Test Method *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="test_method" 
                      value={formData.test_method} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sample Test Code Number *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="sample_test_code" 
                      value={formData.sample_test_code} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required 
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Testing *</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="date_of_testing" 
                      value={formData.date_of_testing} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      required 
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Maximum Nominal Size of Aggregate *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="max_nominal_size" 
                      value={formData.max_nominal_size} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Environmental Conditions *</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="environmental_conditions" 
                      value={formData.environmental_conditions} 
                      onChange={handleInputChange} 
                      className="form-control-vitrag" 
                      placeholder="" 
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Test Result Section */}
      <Row className="mb-4">
        <Col>
          <div style={{ padding: '10px 0' }}>
            <h5 className="mb-0"><FontAwesomeIcon icon={faTable} className="me-2" />Test Result</h5>
          </div>
          
          {/* Main Data Table */}
              <div className="table-responsive">
            <table className="table transparent-table" style={{ fontSize: '1.1rem', tableLayout: 'fixed', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                  <thead className="table-dark">
                    <tr>
                  <th style={{ width: '550px', padding: '6px', textAlign: 'left', verticalAlign: 'middle', fontSize: '1.3rem', border: '1px solid #dee2e6' }}>Number of Tests</th>
                  <th style={{ width: '140px', padding: '4px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.3rem', border: '1px solid #dee2e6' }}>01</th>
                  <th style={{ width: '140px', padding: '4px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.3rem', border: '1px solid #dee2e6' }}>02</th>
                  <th style={{ width: '140px', padding: '4px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.3rem', border: '1px solid #dee2e6' }}>03</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', border: '1px solid #dee2e6' }}>
                    <strong>Wt. of Saturated Surface Dry Aggregate (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(A)</span></strong>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="saturated_weight_01"
                      value={formData.saturated_weight_01}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="saturated_weight_02"
                      value={formData.saturated_weight_02}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="saturated_weight_03"
                      value={formData.saturated_weight_03}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                    </tr>
                    <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', border: '1px solid #dee2e6' }}>
                    <strong>Wt. of Pycnometer + Aggregate + Water (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(B)</span></strong>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="pycnometer_aggregate_water_01"
                      value={formData.pycnometer_aggregate_water_01}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="pycnometer_aggregate_water_02"
                      value={formData.pycnometer_aggregate_water_02}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="pycnometer_aggregate_water_03"
                      value={formData.pycnometer_aggregate_water_03}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                    </tr>
                    <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', border: '1px solid #dee2e6' }}>
                    <strong>Wt. of Pycnometer + Water (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(C)</span></strong>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="pycnometer_water_01"
                      value={formData.pycnometer_water_01}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="pycnometer_water_02"
                      value={formData.pycnometer_water_02}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="pycnometer_water_03"
                      value={formData.pycnometer_water_03}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                    </tr>
                    <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', border: '1px solid #dee2e6' }}>
                    <strong>Wt. of Oven Dried Aggregate (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(D)</span></strong>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="oven_dried_weight_01"
                      value={formData.oven_dried_weight_01}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="oven_dried_weight_02"
                      value={formData.oven_dried_weight_02}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="oven_dried_weight_03"
                      value={formData.oven_dried_weight_03}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ height: '30px' }}
                    />
                  </td>
                    </tr>
                    <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', border: '1px solid #dee2e6' }}>
                    <strong>Specific Gravity = [D/A-(B-C)]</strong>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.001" 
                      value={formData.specific_gravity_01 || ''}
                      readOnly
                      style={{ height: '30px', backgroundColor: '#f8f9fa' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.001" 
                      value={formData.specific_gravity_02 || ''}
                      readOnly
                      style={{ height: '30px', backgroundColor: '#f8f9fa' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.001" 
                      value={formData.specific_gravity_03 || ''}
                      readOnly
                      style={{ height: '30px', backgroundColor: '#f8f9fa' }}
                    />
                  </td>
                    </tr>
                    <tr className="table-info">
                  <td style={{ fontSize: '1rem', padding: '6px', border: '1px solid #dee2e6' }}>
                    <strong>Mean of Specific Gravity</strong>
                  </td>
                  <td colSpan="3" style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.001" 
                      value={formData.mean_specific_gravity || ''}
                      readOnly
                      className="form-control form-control-sm average-input-transparent" 
                      style={{ height: '30px', backgroundColor: 'var(--bs-dark)', background: 'var(--bs-dark)', backgroundImage: 'none', border: '1px solid var(--bs-gray-700)', color: '#ffffff', WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', boxShadow: 'none', outline: 'none' }}
                    />
                  </td>
                    </tr>
                    <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '6px', border: '1px solid #dee2e6' }}>
                    <strong>Water Absorption (%)= [(A-D)/D]*100</strong>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      value={formData.water_absorption_01 || ''}
                      readOnly
                      style={{ height: '30px', backgroundColor: '#f8f9fa' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      value={formData.water_absorption_02 || ''}
                      readOnly
                      style={{ height: '30px', backgroundColor: '#f8f9fa' }}
                    />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      value={formData.water_absorption_03 || ''}
                      readOnly
                      style={{ height: '30px', backgroundColor: '#f8f9fa' }}
                    />
                  </td>
                    </tr>
                    <tr className="table-info">
                  <td style={{ fontSize: '1rem', padding: '6px', border: '1px solid #dee2e6' }}>
                    <strong>Mean of Water Absorption</strong>
                  </td>
                  <td colSpan="3" style={{ padding: '4px', textAlign: 'center', border: '1px solid #dee2e6', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      value={formData.mean_water_absorption || ''}
                      readOnly
                      className="form-control form-control-sm average-input-transparent" 
                      style={{ height: '30px', backgroundColor: 'var(--bs-dark)', background: 'var(--bs-dark)', backgroundImage: 'none', border: '1px solid var(--bs-gray-700)', color: '#ffffff', WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', boxShadow: 'none', outline: 'none' }}
                    />
                  </td>
                    </tr>
                  </tbody>
                </table>
              </div>
        </Col>
      </Row>
    </div>
  );


  const renderRemarks = () => (
    <Row className="mb-4">
      <Col md={12}>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faComment} className="me-2" />
              Remarks
            </h5>
          </Card.Header>
          <Card.Body className="transparent-input-section">
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} name="remarks" value={formData.remarks} onChange={handleInputChange} placeholder="" className="form-control-vitrag"/>
            </Form.Group>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderReviewedBy = () => (
    <Row className="mb-4">
      <Col md={12}>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faUserCheck} className="me-2" />
              Reviewed By
            </h5>
          </Card.Header>
          <Card.Body className="transparent-input-section">
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--vitrag-gold)', fontWeight: '600' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                    Select Reviewer <span className="text-danger">*</span>
                  </Form.Label>
                  {loadingReviewers ? (
                    <div className="d-flex align-items-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span>Loading reviewers...</span>
                    </div>
                  ) : (
                    <>
                      <Form.Select 
                        name="reviewed_by" 
                        value={formData.reviewed_by} 
                        onChange={handleInputChange} 
                        className="form-control-vitrag"
                        required
                      >
                        <option value="">-- Select Reviewer --</option>
                        {reviewers.map((reviewer) => (
                          <option key={reviewer.id} value={reviewer.name}>
                            {reviewer.name}
                          </option>
                        ))}
                      </Form.Select>
                      {/* Display reviewer details when selected */}
                      {formData.reviewer_name && (
                        <div className="mt-2" style={{ fontSize: '0.9rem', color: '#666' }}>
                          <div><strong>Designation:</strong> {formData.reviewer_designation || 'N/A'}</div>
                          <div><strong>Qualification:</strong> {formData.reviewer_graduation || 'N/A'}</div>
                        </div>
                      )}
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--vitrag-gold)', fontWeight: '600' }}>
                    <FontAwesomeIcon icon={faUserShield} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                    Authorized By
                  </Form.Label>
                  <Form.Control 
                    type="text" 
                    name="authorized_by" 
                    value={formData.authorized_by} 
                    onChange={handleInputChange} 
                    className="form-control-vitrag" 
                    readOnly 
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderVerification = () => (
    <Row className="mb-4">
      <Col md={12}>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              Verification
            </h5>
          </Card.Header>
          <Card.Body className="transparent-input-section">
            <Row>
              {/* TESTED BY Column */}
              <Col md={4}>
                <h6 style={{ color: 'var(--vitrag-gold)', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '20px' }}>
                  TESTED BY
                </h6>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--vitrag-gold)', fontWeight: '600' }}>
                    <FontAwesomeIcon icon={faUser} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                    NAME <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control 
                    type="text" 
                    name="tested_by_name" 
                    value={formData.tested_by_name} 
                    onChange={handleInputChange} 
                    className="form-control-vitrag" 
                    placeholder="Enter tester name"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--vitrag-gold)', fontWeight: '600' }}>
                    <FontAwesomeIcon icon={faCalendar} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                    DATE <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control 
                    type="date" 
                    name="tested_by_date" 
                    value={formData.tested_by_date} 
                    onChange={handleInputChange} 
                    className="form-control-vitrag" 
                    required
                  />
                </Form.Group>
              </Col>
              
              {/* CHECKED BY Column */}
              <Col md={4}>
                <h6 style={{ color: 'var(--vitrag-gold)', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '20px' }}>
                  CHECKED BY
                </h6>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--vitrag-gold)', fontWeight: '600' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                    NAME <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control 
                    type="text" 
                    name="checked_by_name" 
                    value={formData.checked_by_name} 
                    onChange={handleInputChange} 
                    className="form-control-vitrag" 
                    placeholder="Enter checker name"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--vitrag-gold)', fontWeight: '600' }}>
                    <FontAwesomeIcon icon={faCalendar} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                    DATE <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control 
                    type="date" 
                    name="checked_by_date" 
                    value={formData.checked_by_date} 
                    onChange={handleInputChange} 
                    className="form-control-vitrag" 
                    required
                  />
                </Form.Group>
              </Col>
              
              {/* VERIFIED BY Column */}
              <Col md={4}>
                <h6 style={{ color: 'var(--vitrag-gold)', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '20px' }}>
                  VERIFIED BY
                </h6>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--vitrag-gold)', fontWeight: '600' }}>
                    <FontAwesomeIcon icon={faUserShield} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                    NAME
                  </Form.Label>
                  <Form.Control 
                    type="text" 
                    name="verified_by_name" 
                    value={formData.verified_by_name} 
                    onChange={handleInputChange} 
                    className="form-control-vitrag" 
                    readOnly
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--vitrag-gold)', fontWeight: '600' }}>
                    <FontAwesomeIcon icon={faCalendar} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                    DATE <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control 
                    type="date" 
                    name="verified_by_date" 
                    value={formData.verified_by_date} 
                    onChange={handleInputChange} 
                    className="form-control-vitrag" 
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    
    // Validate required fields
    if (!formData.customer_id) {
      setSaveError('Please select a customer');
      setTimeout(() => setSaveError(null), 5000);
      return;
    }
    
    if (!selectedTests || selectedTests.length === 0) {
      setSaveError('Please select at least one test');
      setTimeout(() => setSaveError(null), 5000);
      return;
    }
    
    // Reset previous states
    setSaveError(null);
    setSaveSuccess(false);
    setSaving(true);
    
    try {
      // Calculate sieve analysis values synchronously before submission
      let latestFormData = { ...formData };
      
      if (selectedTests.includes('sieveAnalysis')) {
        // Recalculate sieve analysis synchronously
        const weights = [
          parseFloat(formData.sieve_weight_retained_01) || 0,
          parseFloat(formData.sieve_weight_retained_02) || 0,
          parseFloat(formData.sieve_weight_retained_03) || 0,
          parseFloat(formData.sieve_weight_retained_04) || 0,
          parseFloat(formData.sieve_weight_retained_05) || 0,
          parseFloat(formData.sieve_weight_retained_06) || 0,
          parseFloat(formData.sieve_weight_retained_07) || 0,
          parseFloat(formData.sieve_weight_retained_08) || 0,
          parseFloat(formData.sieve_weight_retained_09) || 0
        ];
        
        const sumOfWeights = weights.reduce((sum, val) => sum + val, 0);
        
        if (sumOfWeights > 0) {
          // Calculate % Weight Retained
          const percentWeightRetained = weights.map(weight => (weight / sumOfWeights) * 100);
          percentWeightRetained.forEach((value, index) => {
            latestFormData[`sieve_percent_weight_retained_${String(index + 1).padStart(2, '0')}`] = value > 0 ? value.toFixed(2) : '';
          });
          
          // Calculate Cumulative % Weight Retained
          let cumulativeSum = 0;
          const cumulativePercentRetained = [];
          percentWeightRetained.forEach((value) => {
            cumulativeSum += value;
            cumulativePercentRetained.push(cumulativeSum);
          });
          cumulativePercentRetained.forEach((value, index) => {
            latestFormData[`sieve_cumulative_percent_retained_${String(index + 1).padStart(2, '0')}`] = value > 0 ? value.toFixed(2) : '';
          });
          
          // Calculate % Passing
          cumulativePercentRetained.forEach((value, index) => {
            const percentPassing = 100 - value;
            latestFormData[`sieve_percent_passing_${String(index + 1).padStart(2, '0')}`] = percentPassing >= 0 ? percentPassing.toFixed(2) : '';
          });
          
          // Calculate Sum of Cumulative % Weight Retained (Excluding Pan)
          const sumCumulativeExcludingPan = cumulativePercentRetained.slice(0, 7).reduce((sum, val) => sum + val, 0);
          latestFormData.sieve_sum_cumulative_excluding_pan = sumCumulativeExcludingPan > 0 ? sumCumulativeExcludingPan.toFixed(2) : '';
          
          // Calculate Fineness Modulus
          const finenessModulus = sumCumulativeExcludingPan / 100;
          latestFormData.fineness_modulus = finenessModulus > 0 ? finenessModulus.toFixed(2) : '';
        }
      }
      
      // Ensure reviewer and authorized fields are populated before submission
      if (latestFormData.reviewed_by && !latestFormData.reviewer_name) {
        const selectedReviewer = reviewers.find(r => r.name === latestFormData.reviewed_by);
        if (selectedReviewer) {
          latestFormData.reviewer_name = selectedReviewer.name || latestFormData.reviewed_by;
          latestFormData.reviewer_designation = selectedReviewer.designation || '';
          latestFormData.reviewer_graduation = selectedReviewer.graduation || '';
        }
      }
      
      // Ensure authorized fields have default values if not set
      if (!latestFormData.authorized_name) {
        latestFormData.authorized_name = 'Mr. Prakarsh A Sangave';
      }
      if (!latestFormData.authorized_designation) {
        latestFormData.authorized_designation = 'Chief Executive Officer';
      }
      if (!latestFormData.authorized_qualification) {
        latestFormData.authorized_qualification = 'M.E(Civil-Structures)';
      }
      if (!latestFormData.authorized_qualification_2) {
        latestFormData.authorized_qualification_2 = 'MTech (Civil-Geotechnical), M.I.E, F.I.E.';
      }
      
      console.log('📋 [FORM SUBMIT] Reviewer fields:', {
        reviewed_by: latestFormData.reviewed_by,
        reviewer_name: latestFormData.reviewer_name,
        reviewer_designation: latestFormData.reviewer_designation,
        reviewer_graduation: latestFormData.reviewer_graduation
      });
      
      console.log('📋 [FORM SUBMIT] Authorized fields:', {
        authorized_by: latestFormData.authorized_by,
        authorized_name: latestFormData.authorized_name,
        authorized_designation: latestFormData.authorized_designation,
        authorized_qualification: latestFormData.authorized_qualification
      });
      
      // Prepare data for backend
      const testRequestData = {
        customer_id: latestFormData.customer_id,
        selectedTests: selectedTests,
        formData: latestFormData,
        status: 'completed'
      };
      
      console.log('Saving Fine Aggregate test request:', testRequestData);
      
      // Debug: Log sieve analysis data being sent
      if (selectedTests.includes('sieveAnalysis')) {
        console.log('🔍 [FORM SUBMIT] Sieve Analysis data being sent:', {
          total_weight: latestFormData.total_weight,
          sieve_weight_retained_01: latestFormData.sieve_weight_retained_01,
          sieve_percent_weight_retained_01: latestFormData.sieve_percent_weight_retained_01,
          sieve_cumulative_percent_retained_01: latestFormData.sieve_cumulative_percent_retained_01,
          sieve_percent_passing_01: latestFormData.sieve_percent_passing_01,
          fineness_modulus: latestFormData.fineness_modulus,
          zone_result: latestFormData.zone_result,
          sieve_sum_cumulative_excluding_pan: latestFormData.sieve_sum_cumulative_excluding_pan
        });
      }
      
      // Save to backend
      const response = await createFineAggregateTestRequest(testRequestData);
      console.log('✅ Fine Aggregate test request saved:', response);
      
      // Show success message
      setSaveSuccess(true);
      setSaving(false);
      
      // Auto-dismiss success message and navigate after 2 seconds
      setTimeout(() => {
        navigate('/test-results', {
          state: {
            module: 'fineAggregate',
            selectedTests: selectedTests,
            formData: formData,
            testRequestId: response.test_request_id || response.id
          }
        });
      }, 2000);
    } catch (error) {
      console.error('❌ Error saving Fine Aggregate test:', error);
      setSaving(false);
      setSaveError(error.response?.data?.error || error.message || 'Failed to save Fine Aggregate test. Please try again.');
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setSaveError(null), 5000);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Card className="fine-aggregate-header" style={{ backgroundColor: '#17a2b8 !important', color: 'white !important', border: 'none', borderRadius: '15px' }}>
            <Card.Body className="fine-aggregate-header" style={{ backgroundColor: '#17a2b8 !important', color: 'white !important' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}><FontAwesomeIcon icon={faMountain} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />Fine Aggregate Testing - {currentForm}</h1>
                  <p className="mb-0 mt-2" style={{ color: 'white' }}>
                    {currentForm === 1 && "Determination of Bulk Density of Aggregate (Rodded & Loose)"}
                    {currentForm === 2 && "Sieve Analysis of Fine Aggregate"}
                    {currentForm === 3 && "Determination of Specific Gravity & Water Absorption of Fine Aggregate"}
                  </p>
                </div>
                <div>
                  {/* Back button removed as per request */}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
        <Form onSubmit={handleSubmit}>
          {/* Success Alert */}
          {saveSuccess && (
            <Row className="mb-3">
              <Col>
                <Alert variant="success" className="d-flex align-items-center" onClose={() => setSaveSuccess(false)} dismissible>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" style={{ fontSize: '1.2rem' }} />
                  <strong>Success!</strong> Fine Aggregate test data saved successfully. Redirecting to test results...
                </Alert>
              </Col>
            </Row>
          )}
          
          {/* Error Alert */}
          {saveError && (
            <Row className="mb-3">
              <Col>
                <Alert variant="danger" className="d-flex align-items-center" onClose={() => setSaveError(null)} dismissible>
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" style={{ fontSize: '1.2rem' }} />
                  <strong>Error!</strong> {saveError}
                </Alert>
              </Col>
            </Row>
          )}
          
          {/* Selected Tests Display - Moved to top */}
          {selectedTests.length > 0 && (
            <Row className="mb-4">
              <Col>
                <Card className="shadow-sm card-vitrag" style={{ backgroundColor: 'var(--vitrag-secondary)', border: '1px solid var(--vitrag-border)' }}>
                  <Card.Body className="py-3">
                    <h6 className="mb-3" style={{ color: 'var(--vitrag-gold)' }}>
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                      Selected Tests:
                    </h6>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedTests.map((testId, index) => {
                        let title = '';
                        if (testId === 'bulkDensity') title = 'Bulk Density';
                        else if (testId === 'sieveAnalysis') title = 'Sieve Analysis';
                        else if (testId === 'specificGravity') title = 'Specific Gravity & Water Absorption';
                        
                        return (
                          <span key={index} className="badge me-2 mb-1 px-3 py-2" style={{ fontSize: '0.9rem', backgroundColor: 'var(--vitrag-primary)', color: 'white' }}>
                            {title}
                          </span>
                        );
                      })}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Form Navigation */}
          <Row className="mb-2">
            <Col>
              <Card className="step-navigation" style={{ border: 'none' }}>
                <Card.Body className="py-2">
                  <div className="d-flex justify-content-center">
                    {selectedTests.includes('bulkDensity') && (
                      <Button
                        variant={currentForm === 1 ? "primary" : "outline-primary"}
                        className="btn-lg me-5"
                        style={{ fontSize: '1.5rem', fontWeight: 'bold', width: '80px', height: '80px', borderRadius: '50%', marginRight: '5rem !important', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => setCurrentForm(1)}
                      >
                        1
                      </Button>
                    )}
                    {selectedTests.includes('sieveAnalysis') && (
                      <Button
                        variant={currentForm === 2 ? "primary" : "outline-primary"}
                        className="btn-lg mx-5"
                        style={{ fontSize: '1.5rem', fontWeight: 'bold', width: '80px', height: '80px', borderRadius: '50%', marginLeft: '5rem !important', marginRight: '5rem !important', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => setCurrentForm(2)}
                      >
                        2
                      </Button>
                    )}
                    {selectedTests.includes('specificGravity') && (
                      <Button
                        variant={currentForm === 3 ? "primary" : "outline-primary"}
                        className="btn-lg ms-5"
                        style={{ fontSize: '1.5rem', fontWeight: 'bold', width: '80px', height: '80px', borderRadius: '50%', marginLeft: '5rem !important', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => setCurrentForm(3)}
                      >
                        3
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Render current form */}
          {selectedTests.length === 0 ? (
            <Row className="mb-4">
              <Col>
                <Alert variant="warning" className="text-center">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  No tests selected. Please go back and select at least one test to perform.
                </Alert>
              </Col>
            </Row>
          ) : (
            <>
              {currentForm === 1 && selectedTests.includes('bulkDensity') && renderForm1()}
              {currentForm === 2 && selectedTests.includes('sieveAnalysis') && renderForm2()}
              {currentForm === 3 && selectedTests.includes('specificGravity') && renderForm3()}
            </>
          )}

          {/* Verification, Remarks, and Reviewed By - Only show on last step */}
          {(() => {
            // Determine the last available step
            let lastStep = 1;
            if (selectedTests.includes('specificGravity')) lastStep = 3;
            else if (selectedTests.includes('sieveAnalysis')) lastStep = 2;
            else if (selectedTests.includes('bulkDensity')) lastStep = 1;

            if (currentForm === lastStep) {
              return (
                <>
                  {renderVerification()}
                  {renderReviewedBy()}
                  {renderRemarks()}
                </>
              );
            }
            return null;
          })()}

          {/* Submit Buttons */}
          <Row>
            <Col className="text-center">
              <div className="d-grid gap-2 d-md-block">
                {/* Cancel button removed */}
                {(() => {
                  // Determine the last available step
                  let lastStep = 1;
                  if (selectedTests.includes('specificGravity')) lastStep = 3;
                  else if (selectedTests.includes('sieveAnalysis')) lastStep = 2;
                  else if (selectedTests.includes('bulkDensity')) lastStep = 1;

                  if (currentForm < lastStep) {
                    return (
                      <Button 
                        variant="primary" 
                        className="btn-lg me-md-2 btn-vitrag-primary" 
                        onClick={() => {
                          // Find next available step
                          let nextStep = currentForm + 1;
                          while (nextStep <= 3) {
                            if (
                              (nextStep === 2 && selectedTests.includes('sieveAnalysis')) ||
                              (nextStep === 3 && selectedTests.includes('specificGravity'))
                            ) {
                              break;
                            }
                            nextStep++;
                          }
                          if (nextStep <= 3) setCurrentForm(nextStep);
                        }}
                        disabled={saving || saveSuccess}
                      >
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        Save and Next
                      </Button>
                    );
                  } else {
                    return (
                      <Button 
                        variant="success" 
                        type="submit" 
                        className="btn-lg btn-vitrag-primary"
                        disabled={saving || saveSuccess}
                      >
                        {saving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" style={{ color: 'white' }} />
                            Saving to Database...
                          </>
                        ) : saveSuccess ? (
                          <>
                            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                            Saved Successfully!
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            Save Test Data
                          </>
                        )}
                      </Button>
                    );
                  }
                })()}
              </div>
            </Col>
          </Row>
        </Form>
    </div>
  );
};

export default FineAggregateForm;
